import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import {
  LeftOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  RightOutlined,
} from '@ant-design/icons';
import {
  Layout as AntdLayout,
  App,
  Button,
  ConfigProvider,
  Drawer,
  Flex,
} from 'antd';
import Breadcrumb, { ItemType } from 'antd/es/breadcrumb/Breadcrumb';
import Title from 'antd/es/typography/Title';
import enUS from 'antd/locale/en_US';
import esES from 'antd/locale/es_ES';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { useLocation } from 'react-router-dom';

import { Company } from 'models/Company';
import { DashboardConfig } from 'models/DashboardConfig';
import { MenuNode } from 'models/MenuNode';
import { Role } from 'models/Role';
import { User } from 'models/User';

import { CompanyService } from 'services/CompanyService';
import { RoleService } from 'services/RoleService';

import { FunctionsHelper } from 'utils/FunctionsHelper';

import { USER_TYPES } from '../../config/Constants';
import { useThemeLayoutConfig } from '../../config/ThemeDashboardLayoutConfig';
import LoadingScreen from '../LoadingScreen/LoadingScreen';
import NavigationMenuTree from './components/NavigationMenuTree';
import ProfileMenu from './components/ProfileMenu';
import TimeZoneBadge from './components/TimeZoneBadge';

import './Layout.scss';

interface LayoutProps {
  children: ReactNode;
  loading?: boolean;
  title?: string | React.ReactNode;
  breadcrumbMode?: 'none' | 'compact' | 'full';
}

const { Header, Sider } = AntdLayout;

function Layout({
  children,
  loading = false,
  title = undefined,
  breadcrumbMode = 'compact',
}: LayoutProps) {
  const { i18n } = useTranslation();
  const customConfig = useThemeLayoutConfig();
  const dashboardConfig: DashboardConfig = useSelector(
    (state: any) => state.dashboardConfig
  );
  const authUser: User = useSelector((state: any) => state.auth);
  const location = useLocation();
  const intlLang: string = useSelector(
    (state: any) => state.intl?.lang || i18n.language
  );
  const isDesktop = useMediaQuery({ query: '(min-width: 768px)' });
  const [localLoading, setLocalLoading] = useState(false);
  const [isToggled, setToggled] = useState(false);
  const toggleTrueFalse = () => setToggled(!isToggled);
  const [siderCollapsed, setSiderCollapsed] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [allRoles, setAllRoles] = useState<Role[]>([]);

  // Track header height to constrain content/sider height and make menu scroll independent
  const headerMeasureRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState<number>(64);

  const antdLocale = useMemo(() => {
    switch (intlLang) {
      case 'en':
        return enUS;
      case 'es':
      default:
        return esES;
    }
  }, [intlLang]);

  useEffect(() => {
    const updateHeaderHeight = () => {
      if (headerMeasureRef.current) {
        setHeaderHeight(headerMeasureRef.current.offsetHeight);
      }
    };
    updateHeaderHeight();
    window.addEventListener('resize', updateHeaderHeight);
    return () => window.removeEventListener('resize', updateHeaderHeight);
  }, []);

  useEffect(() => {
    if (
      [USER_TYPES.SUPER_ADMIN, USER_TYPES.ADMIN].includes(authUser.userType)
    ) {
      Promise.all([
        CompanyService.getAllFilteredByCurrentUser(),
        RoleService.getAll(),
      ]).then(([companiesRes, rolesRes]) => {
        if (companiesRes.success) {
          setCompanies((companiesRes.data as Company[]) || []);
        }
        if (rolesRes.success) {
          setAllRoles((rolesRes.data as Role[]) || []);
        }
      });
    }
  }, [authUser.userType]);

  const [computedTitle, setComputedTitle] = useState<string>('');
  const [computedBreadcrumb, setComputedBreadcrumb] = useState<ItemType[]>([]);

  useEffect(() => {
    const menusTree = (authUser as any)?.menus || ([] as MenuNode[]);
    const full = breadcrumbMode === 'full';
    const result = FunctionsHelper.buildBreadcrumbForPath(
      menusTree,
      location.pathname,
      {
        includeAllParents: full,
        includeHome: false,
        showLeafIcon: true,
        parentsMode: full ? 'full' : 'compact',
        inheritPrevContainerSibling: true,
        preferParentCodeAscend: true,
      }
    );
    setComputedTitle(result?.title || '');
    setComputedBreadcrumb(result?.breadcrumb || []);
  }, [location.pathname, authUser.id, i18n.language, breadcrumbMode]);

  useEffect(() => {
    if (headerMeasureRef.current) {
      setHeaderHeight(headerMeasureRef.current.offsetHeight);
    }
  }, [isDesktop, isToggled]);

  const onClose = () => {
    setToggled(false);
  };

  // Una vista puede pasar `title=""` para quedarse solo con el breadcrumb; con
  // `undefined` se cae al título que sale del árbol de menús.
  const resolvedTitle = title !== undefined ? title : computedTitle;
  const showBreadcrumb =
    breadcrumbMode !== 'none' && computedBreadcrumb.length > 0;

  return (
    <ConfigProvider locale={antdLocale} theme={customConfig.theme}>
      <App>
        <LoadingScreen loading={loading || localLoading} />
        <AntdLayout style={{ minHeight: '100vh' }}>
          {!isDesktop && (
            <Drawer
              placement="left"
              onClose={onClose}
              closable={false}
              open={isToggled}
              styles={{
                body: {
                  backgroundColor:
                    customConfig.theme.token?.colorPrimary || '#FFF',
                  padding: '0',
                },
              }}
              width="80%"
            >
              <Flex
                gap="middle"
                justify="center"
                align="center"
                style={{ cursor: 'pointer' }}
              >
                <img
                  className="logo"
                  src={dashboardConfig.logoUrl}
                  style={{
                    height: `${dashboardConfig.logoHeight + 4}px`,
                    padding: '10px',
                  }}
                />
              </Flex>
              <NavigationMenuTree
                style={{ height: '100%', borderRight: 0, padding: '4px 4px' }}
                mode="inline"
              />
            </Drawer>
          )}
          <Header
            className="activation-header"
            style={{ backgroundColor: customConfig.theme.token?.colorPrimary }}
          >
            <div ref={headerMeasureRef} style={{ width: '100%' }}>
              <Flex
                gap="middle"
                justify="space-between"
                style={{ width: '100%' }}
              >
                {!isDesktop &&
                  React.createElement(
                    isToggled ? MenuUnfoldOutlined : MenuFoldOutlined,
                    {
                      className: 'trigger-menu-layout',
                      onClick: toggleTrueFalse,
                    }
                  )}
                <Flex
                  gap="middle"
                  justify="space-between"
                  align="center"
                  style={{ cursor: 'pointer' }}
                >
                  <img
                    className="logo"
                    src={dashboardConfig.logoUrl}
                    style={{ height: `${dashboardConfig.logoHeight}px` }}
                  />
                </Flex>

                <Flex gap="middle" align="center">
                  <TimeZoneBadge />
                  <ProfileMenu
                    loading={localLoading}
                    setLoading={setLocalLoading}
                    companies={companies}
                    roles={allRoles}
                  />
                </Flex>
              </Flex>
            </div>
          </Header>
          <AntdLayout
            style={{
              height: `calc(100vh - ${headerHeight}px)`,
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            {isDesktop && (
              <Sider
                width={320}
                style={{
                  background: customConfig.theme.token?.colorBgContainer,
                }}
                collapsible
                trigger={null}
                collapsed={siderCollapsed}
                collapsedWidth={64}
              >
                <NavigationMenuTree
                  style={{
                    height: `calc(100vh - ${headerHeight}px)`,
                    overflowY: 'auto',
                    borderRight: 0,
                    padding: '4px 4px',
                  }}
                  className="sider-menu-scroll"
                  mode="inline"
                />
              </Sider>
            )}
            {isDesktop && (
              <Button
                shape="circle"
                size="small"
                className="sider-toggle-floating"
                icon={siderCollapsed ? <RightOutlined /> : <LeftOutlined />}
                onClick={() => setSiderCollapsed(!siderCollapsed)}
                style={{
                  position: 'absolute',
                  top: 12,
                  left: siderCollapsed ? 64 : 320,
                  transform: 'translate(-50%, 0)',
                  zIndex: 30,
                }}
              />
            )}
            <AntdLayout
              style={{
                padding: '18px 24px',
                height: `calc(100vh - ${headerHeight}px)`,
                overflow: 'auto',
              }}
              className="content-scroll"
            >
              {/* El contenedor se muestra solo si hay algo que poner adentro.
                  Antes bastaba con que `title` viniera definido, así que una
                  vista que lo pasa vacío para quedarse solo con el breadcrumb
                  dejaba una caja sin contenido con sus 24px de margen. */}
              {(showBreadcrumb || resolvedTitle) && (
                <Flex
                  gap="small"
                  justify="flex-start"
                  vertical
                  className="dashboard-layout-header-container"
                >
                  {showBreadcrumb && (
                    <Flex
                      gap="middle"
                      justify="flex-start"
                      className="dashboard-layout-breadcrumb"
                    >
                      <Breadcrumb separator=">" items={computedBreadcrumb} />
                    </Flex>
                  )}
                  {resolvedTitle && (
                    <Flex
                      gap="middle"
                      justify="flex-start"
                      className="dashboard-layout-title"
                    >
                      <Title
                        level={3}
                        style={{
                          textTransform: 'uppercase',
                          marginBottom: '0',
                        }}
                      >
                        {resolvedTitle}
                      </Title>
                    </Flex>
                  )}
                  {/*<Divider style={{ marginTop: '0', marginBottom: '0' }}/>*/}
                </Flex>
              )}
              {children}
            </AntdLayout>
          </AntdLayout>
        </AntdLayout>
      </App>
    </ConfigProvider>
  );
}

export default Layout;
